import {Component, Host, h, Prop, State, Event, EventEmitter, Element, Watch} from '@stencil/core';
import type {SolIconNames} from '../sol-icon/types/sol-icon-names';
/**
 * @deprecated PIX remove component
 */
@Component({
  tag: 'sol-search-input',
  styleUrl: 'sol-search-input.scss',
  shadow: true,
})
export class SolSearchInput {
  searchInputElement!: HTMLElement;

  searchInputListElement!: HTMLElement;

  @Prop() value?: string;

  @Prop() searchItems: any[] = [];

  @Prop() searchBy: string = '';

  @Prop() label: string = '';

  @Prop() minChar: number = 3;

  @Prop() placeholder: string = 'Search...';

  @Prop() loadingText: string = 'Loading';

  @Prop() readonly: boolean = false;

  @Prop() isDynamicSearchList: boolean = false;

  @Prop() disabled: boolean = false;

  @Prop() isLoading: boolean = false;

  @Prop() closeWithScroll: boolean = true;

  @Prop() hasIconLeft: boolean = false;

  @Prop() hasIconRight: boolean = false;

  @Prop() messageErrorMinChar?: string;

  @Prop() icon: SolIconNames = 'search';

  @Prop() isOpaque: boolean = false;

  @State() _displayList = false;

  @State() searchInputValue = '';

  @State() _searchFilteredList: any[] = [];

  private _checkClickOutBind: any;

  @Element() private element?: HTMLElement;

  @Event({
    eventName: 'update-value',
  })
  selectedSearchEmitter?: EventEmitter<any>;

  @Event({
    eventName: 'search-local',
  })
  searchLocalEvent?: EventEmitter<any>;

  @Event({
    eventName: 'search-async',
  })
  searchOutSideEvent?: EventEmitter<any>;

  @Event({
    eventName: 'update-input',
  })
  searchInputValueEvent?: EventEmitter<any>;

  @Watch('searchItems')
  onSearchItemsChange() {
    this.staticListFilter();
  }

  componentDidRender() {
    this.setSearchListPosition();

    /* We capture any type of scrolling that is performed with respect to dropdown.
      If we have the property closeWithScroll = true, we will close the dropdown when scrolling.
      Otherwise, the position of the options list will be calculated with respect to the dropdown input,
      so that it will be positioned and displayed above other components, , including the container. */
    document.addEventListener(
      'scroll',
      () => {
        this.closeWithScroll ? (this._displayList = false) : this.setSearchListPosition();
      },
      true
    );
  }

  setSearchListPosition() {
    const {top, height, left} = this.searchInputElement.getBoundingClientRect();

    if (!this.searchInputListElement?.style) return;

    this.searchInputListElement.style.left = `${left}px`;
    this.searchInputListElement.style.top = `${top + height + 5}px`;
  }

  getSearchInputLabel(): HTMLElement {
    return this.label ? (
      <div
        class={{
          'sol-search-input__label': true,
        }}
      >
        <span>{this.label}</span>
      </div>
    ) : null;
  }

  getSearchInput(): HTMLElement {
    return (
      <div
        class={{
          'sol-search-input': true,
          'sol-search-input__container-input-unfilled': !this.value,
          'sol-search-input__container-input-filled': !!this.value,
          'sol-search-input__container-input--disabled': this.disabled,
          'sol-search-input__container-input--is-opaque': this.isOpaque,
          'sol-search-input__has-label-space': !!this.label,
        }}
      >
        {this.hasIconLeft ? <span class={`sol-icon-${this.icon} sol-search-input__has-icon-left`} /> : null}
        {this.isLoading ? (
          <span>{this.loadingText} </span>
        ) : (
          <input
            class={{'solTextField_input--input-readonly': !!this.readonly}}
            disabled={this.disabled}
            readonly={this.readonly}
            placeholder={this.placeholder}
            value={this.value}
            type="text"
            autocomplete="off"
            onInput={(e) => this.handleOnInput(e)}
          />
        )}
        {this.hasIconRight ? <span class={`sol-icon-${this.icon} sol-search-input__has-icon-right`} /> : null}
      </div>
    );
  }

  getMessageErrorMinchart() {
    return (
      <div>
        {this.searchInputValue.length > 1 && this.searchInputValue.length < this.minChar ? (
          <span>{this.messageErrorMinChar}</span>
        ) : null}
      </div>
    );
  }

  handleOnInput(e: any) {
    this.searchInputValue = e.target.value;

    this._checkClickOutBind = this.checkClickOut.bind(this);
    document.addEventListener('click', this._checkClickOutBind);

    if (this.searchInputValue.length >= this.minChar) {
      this._displayList = true;
      if (this.isDynamicSearchList) {
        this.searchOutside();
      } else {
        this.staticListFilter();
      }
    } else {
      this._searchFilteredList = [];
    }

    this.searchInputValueEvent?.emit({
      searchText: this.searchInputValue,
      searchList: this.searchItems,
    });
  }

  checkClickOut(event: Event) {
    const composedPath = event.composedPath();
    let isOwnNode = this.element?.contains(event.target as Node);

    if (composedPath.length) {
      isOwnNode = composedPath.includes(this.element as EventTarget) || this.element?.contains(composedPath[0] as Node);
    }

    if (!isOwnNode) {
      document.removeEventListener('click', this._checkClickOutBind);
      this._displayList = false;
    }
  }

  searchOutside() {
    this.searchOutSideEvent?.emit({
      searchText: this.searchInputValue,
      searchList: this.searchItems,
      isLoading: this.isLoading,
    });
  }

  getNormalizedString(str: string) {
    return str
      ?.trim()
      .replace(/\s|_|\(|\)/g, ' ')
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .toLowerCase();
  }

  strIncludesSubstr(str: string, substr = '') {
    return this.getNormalizedString(str).includes(this.getNormalizedString(substr));
  }

  staticListFilter() {
    this._searchFilteredList = this.searchItems?.filter((searchItem) =>
      this.strIncludesSubstr(searchItem[this.searchBy], this.searchInputValue)
    );

    this.searchLocalEvent?.emit({
      searchText: this.searchInputValue,
      filteredList: this._searchFilteredList,
    });
  }

  getSearchOptionsList(): HTMLElement {
    const listContainerStyle = {
      width: this.searchInputElement?.clientWidth ? `${this.searchInputElement?.clientWidth}px` : 'auto',
    };

    return this._displayList && this._searchFilteredList.length ? (
      <div
        class={{
          'sol-search-input__list-container': true,
        }}
        style={listContainerStyle}
        ref={(el) => (this.searchInputListElement = el as HTMLElement)}
      >
        {this._searchFilteredList.map((item) => (
          <div
            class={{
              'sol-search-input__list-content': true,
            }}
            key={item.id}
          >
            {this.getSelectOptions(item)}
          </div>
        ))}
      </div>
    ) : null;
  }

  getSelectOptions(option: any): HTMLElement {
    return (
      <div
        class={{
          'sol-search-input__list-item': true,
        }}
        onClick={() => this.handleOnclickSearch(option)}
      >
        <span
          title={option[this.searchBy]}
          class={{
            'sol-search-input__list-text': true,
          }}
        >
          {option[this.searchBy]}
        </span>
      </div>
    );
  }

  handleOnclickSearch(option: any): void {
    this.selectedSearchEmitter?.emit(option[this.searchBy]);
    this._displayList = false;
  }

  render() {
    return (
      <Host>
        <div
          ref={(el) => (this.searchInputElement = el as HTMLElement)}
          class={{
            'sol-search-input__container': true,
          }}
        >
          {this.getSearchInputLabel()}
          {this.getSearchInput()}
          {this.getMessageErrorMinchart()}
          {this.getSearchOptionsList()}
        </div>
      </Host>
    );
  }
}
